import { isAxiosError } from 'axios';
import { makeAutoObservable } from 'mobx';

import {
  CatalogRequests,
  type FilterChoiceAPI,
  type ProductsPagedResponse,
  type SortingFieldName,
  type SortingOrder,
} from '~/api/Catalog';
import { catalogStore, mainStore, userStore } from '~/stores';
import type { ProductFilters, ProductRangeFilter } from '~/stores/CatalogStore';
import { parseDiscountedFilter } from '~/stores/CatalogStore/discountedFilter';
import { Category } from '~/stores/CategoriesStore/Category';
import {
  ignoreParams,
  parseProperties,
} from '~/stores/CategoriesStore/catalogSearchParams';
import { Product } from '~/stores/CategoriesStore/Product';
import {
  shrinkCategoryTreeImages,
  shrinkProductImages,
} from '~/stores/CategoriesStore/shrinkImages';
import { Pagination } from '~/stores/shared/Pagination';
import {
  type BackendRedirectResponse,
  isBackendRedirect,
  resolveBackendRedirect,
} from '~/utils/backendRedirect';
import { catalogWarehouseParam } from '~/utils/catalogWarehouse';
import { getCookieLang } from '~/utils/cookies';
import { PreloadCache } from '~/utils/preloadCache';

const PRELOAD_CACHE_MAX = 500;
const PRELOAD_CACHE_TTL_MS = 60_000;

/**
 * How many categories keep their loaded pages once the reader leaves them.
 *
 * Each entry holds every product of every page the reader scrolled through, so
 * the bound is what keeps a long session from growing without end. Five covers
 * the way readers actually browse — a couple of categories deep, then back.
 */
const INSTANCE_CACHE_MAX = 5;

/**
 * How long those pages stay worth showing. Prices and stock move underneath a
 * list that is only held in memory, so a return after this long starts over.
 */
const INSTANCE_CACHE_TTL_MS = 10 * 60_000;

export { ignoreParams };

type Sorting = [SortingFieldName, SortingOrder];

interface ProductParams {
  sorting?: Nullable<Sorting>;
  filters?: Nullable<ProductFilters>;
}

/**
 * Thrown by `CatalogCategory.preload` when the backend response identifies
 * the category as deleted/moved and asks us to redirect the client.
 *
 * `targetUrl === null` means the payload was detected but could not be
 * resolved (unknown type, empty slug, or self-loop). Callers should fall
 * back to a regular 404 path in that case.
 */
export class CategoryRedirectError extends Error {
  constructor(
    public readonly payload: BackendRedirectResponse,
    public readonly targetUrl: string | null,
  ) {
    super('Backend redirect for category');
    this.name = 'CategoryRedirectError';
  }
}

export class CatalogCategory {
  private static instances = CatalogCategory.createInstanceCache();
  private category: Nullable<Category> = null;
  private readonly id: string | number;
  private preloadNotFound = false;
  /**
   * Warehouse active when the data was loaded; the instance outlives the
   * page. Used only for staleness — the request itself carries no warehouse
   * code (the response holds stock for all active warehouses).
   */
  private loadedWarehouse: Nullable<string> = null;
  /**
   * Request parameters the loaded pages belong to.
   *
   * The pages are only worth handing back on a return if the page would ask
   * the backend for the same thing now — same filters, same sorting, same
   * language, same reader. `null` while nothing has been loaded through
   * `paginationRequest`, which is also the state SSR-preloaded data lands in.
   */
  private loadedParams: Nullable<string> = null;

  private static preloadedData = new PreloadCache<ProductsPagedResponse>({
    max: PRELOAD_CACHE_MAX,
    ttlMs: PRELOAD_CACHE_TTL_MS,
  });
  private static preloadedNotFound = new PreloadCache<true>({
    max: PRELOAD_CACHE_MAX,
    ttlMs: PRELOAD_CACHE_TTL_MS,
  });

  constructor(categoryId: string | number) {
    this.id = categoryId;
    this.init();
    makeAutoObservable(this);
  }

  private static createInstanceCache() {
    return new PreloadCache<CatalogCategory>({
      max: INSTANCE_CACHE_MAX,
      ttlMs: INSTANCE_CACHE_TTL_MS,
    });
  }

  /**
   * The category, keeping the pages it had when the reader last left it.
   *
   * A single instance used to be held here, so opening any other category threw
   * away everything the previous one had loaded — including when it was opened
   * only to read its name for the page header. Going back then started the list
   * from page one, halfway down a list the reader had scrolled.
   */
  public static getInstance(categoryId: string | number): CatalogCategory {
    const key = String(categoryId);
    const cached = CatalogCategory.instances.get(key);

    if (cached) {
      return cached;
    }

    const instance = new CatalogCategory(categoryId);

    CatalogCategory.instances.set(key, instance);

    return instance;
  }

  public static clearAllInstances() {
    CatalogCategory.instances = CatalogCategory.createInstanceCache();
  }

  public static request(
    categoryId: string | number,
    page: number,
    serverLang?: string,
    { filters, sorting }: ProductParams = {},
    warehouse?: string,
  ) {
    const params = catalogStore.getProductFilters(
      filters || catalogStore.currentFilters,
      sorting || catalogStore.currentSorting,
      page,
    );

    return CatalogRequests.getProductsPaged(
      String(categoryId),
      {
        ...catalogWarehouseParam(warehouse),
        isSafeRequest: mainStore.isSafeRequest || undefined,
        ...params,
      },
      serverLang,
      // Accept HTTP 301 with body { redirectTo, reason } as a successful
      // response so preload can inspect the payload instead of axios
      // following the Location header (or throwing).
      {
        maxRedirects: 0,
        validateStatus: (status) =>
          (status >= 200 && status < 300) || status === 301,
      },
    ).then((response) => {
      // A 301 answers with { redirectTo, reason } and carries no listing, so
      // guard rather than trust the success-shaped type.
      shrinkProductImages(response.data?.products);
      shrinkCategoryTreeImages(response.data?.children);

      return response;
    });
  }

  public static async preload(
    categoryId: string | number,
    serverLang?: string,
    params?: ProductParams,
    warehouse?: string,
  ) {
    try {
      const data = await CatalogCategory.request(
        categoryId,
        1,
        serverLang,
        params,
        warehouse,
      );

      if (isBackendRedirect(data)) {
        const targetUrl = resolveBackendRedirect(data, `/c/${categoryId}`);
        throw new CategoryRedirectError(data, targetUrl);
      }

      CatalogCategory.preloadedData.set(String(categoryId), data);
      CatalogCategory.preloadedNotFound.delete(String(categoryId));

      return data;
    } catch (error) {
      if (error instanceof CategoryRedirectError) {
        throw error;
      }
      if (isAxiosError(error) && error.response?.status === 404) {
        CatalogCategory.preloadedNotFound.set(String(categoryId), true);
      }
      throw error;
    }
  }

  public static removePreloadedData(categoryId: string | number) {
    CatalogCategory.preloadedData.delete(String(categoryId));
    CatalogCategory.preloadedNotFound.delete(String(categoryId));
  }

  public static parseSearchParams(
    search: URLSearchParams | string,
  ): ProductParams {
    try {
      const searchParams = toSearchParams(search);
      const sorting = parseSorting(searchParams);
      const properties = parseProperties(searchParams);
      const price = parsePrice(searchParams);
      const discountPrice = parseDiscountedFilter(searchParams);

      return {
        sorting,
        filters: { properties, price, ...(discountPrice && { discountPrice }) },
      };
    } catch {
      return {};
    }
  }

  public get data(): Nullable<Omit<Category, 'products' | 'productsRequest'>> {
    return this.category;
  }

  public get products() {
    return this.paginationRequest.result;
  }

  public get types(): FilterChoiceAPI[] {
    if (!this.category) {
      return [];
    }

    return this.category.subcategory.map(
      ({ id, name, subcategory, productsCount }) => ({
        label: name,
        value: id.toString(),
        count: productsCount,
        isFinal: !subcategory.length,
      }),
    );
  }

  public get isNotFound() {
    if (this.preloadNotFound) {
      return true;
    }
    return isAxiosError(this.paginationRequest.lastError)
      ? this.paginationRequest.lastError.response?.status === 404
      : false;
  }

  public get isReady() {
    return Boolean(this.category);
  }

  /**
   * Whether the pages already loaded are the ones this page would ask for now.
   *
   * What makes a cached list safe to hand back is not that it exists but that
   * nothing about the request moved underneath it: the warehouse decides which
   * products exist and at what price, the language decides how they read, and
   * signing in or out changes both. Filters and sorting decide which products
   * belong in the list at all.
   */
  public matches(warehouse: string): boolean {
    return (
      Boolean(this.category) &&
      this.loadedWarehouse === warehouse &&
      this.loadedParams !== null &&
      this.loadedParams === CatalogCategory.paramsSnapshot()
    );
  }

  /**
   * Canonical form of everything that decides what the listing request returns.
   *
   * Normalised rather than stringified as-is: the same filters reach this from
   * the store as an object and from the URL as parsed params, and two spellings
   * of one filter set would read as a change and throw away a good list.
   *
   * @param params Parameters to describe instead of the ones currently in the
   *   store — used for data that arrived before the store caught up, as SSR
   *   preloaded data does.
   */
  private static paramsSnapshot(params?: ProductParams): string {
    const filters = params?.filters ?? catalogStore.currentFilters;
    const sorting = params?.sorting ?? catalogStore.currentSorting;
    const price = filters?.price;

    return JSON.stringify({
      properties: Object.entries(filters?.properties ?? {})
        .filter(([, value]) => value !== undefined)
        .sort(([left], [right]) => left.localeCompare(right)),
      price:
        price?.from || price?.to
          ? [price.from ?? null, price.to ?? null]
          : null,
      discounted: Boolean(filters?.discountPrice),
      sorting: sorting ?? catalogStore.defaultSorting,
      lang: getCookieLang() || userStore.lang || '',
      authorized: userStore.isAuthorized,
    });
  }

  public readonly paginationRequest = new Pagination(
    async (page, warehouse: string) => {
      const isSSR = typeof window === 'undefined';
      if (!isSSR) {
        delete window.__INITIAL_STATE__?.catalogCategory;
      }
      const { data, pagination } = await CatalogCategory.request(this.id, page);

      this.category = new Category(data);
      this.loadedWarehouse = warehouse;
      this.loadedParams = CatalogCategory.paramsSnapshot();

      return {
        pagination,
        data: data.products?.map((data) => new Product(data)) || [],
      };
    },
    { debounce: 700 },
  );

  private init() {
    const isSSR = typeof window === 'undefined';

    if (isSSR && CatalogCategory.preloadedNotFound.has(String(this.id))) {
      this.preloadNotFound = true;
      return;
    }

    const preloadedData =
      (!isSSR ? window.__INITIAL_STATE__?.catalogCategory : null) ||
      CatalogCategory.preloadedData.get(String(this.id));

    if (!preloadedData || preloadedData.data.category.slug !== this.id) {
      return;
    }

    const { data, pagination } = preloadedData;

    this.category = new Category(data);
    this.loadedWarehouse = isSSR
      ? null
      : (window.__INITIAL_STATE__?.context?.serverWarehouse ?? null);
    // Described from the URL rather than from the store: the server rendered
    // this page for the params in the address bar, while the store still holds
    // whatever the previous page left in it — and, for filters, whatever
    // localStorage restored.
    this.loadedParams = isSSR
      ? null
      : CatalogCategory.paramsSnapshot(
          CatalogCategory.parseSearchParams(window.location.search),
        );
    this.paginationRequest.setState({
      pagination,
      data: data.products?.map((data) => new Product(data)) || [],
    });
  }
}

const parsePrice = (searchParams: URLSearchParams) => {
  const price: ProductRangeFilter = {};
  const priceFrom = +String(searchParams.get('priceFrom'));
  const priceTo = +String(searchParams.get('priceTo'));

  if (priceFrom) {
    price.from = priceFrom;
  }

  if (priceTo) {
    price.to = priceTo;
  }

  return price;
};

const parseSorting = (searchParams: URLSearchParams) => {
  const sorting = searchParams.get('sorting');
  const sortingDir = searchParams.get('sortingDir');

  if (sorting && sortingDir) {
    return [sorting, sortingDir] as [SortingFieldName, SortingOrder];
  }
};

const toSearchParams = (url: string | URLSearchParams): URLSearchParams => {
  if (url instanceof URLSearchParams) {
    return url;
  }

  const search = url.split('?')[1];

  return new URLSearchParams(search);
};
